import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import useAuth from 'src/hooks/useAuth';

interface BillingGuardProps {
  children: React.ReactNode;
}

const BillingGuard: React.FC<BillingGuardProps> = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const { user } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only perform billing checks if user is logged in and we have user data
    if (!isLoggedIn || !user) {
      return;
    }

    // Check if user needs to add payment method or is past due
    const needsPayment = user.billing && (
      (!user.billing.has_payment_method && user.billing.requires_payment_method) ||
      user.billing.past_due
    );

    if (needsPayment) {
      // Allow access to billing-related pages and authentication pages
      const allowedPaths = [
        '/billing-and-plans',
        '/choose-your-plan',
        '/pricing',
        '/subscription/success',
        '/checkout/cancel',
        '/logout',
        '/login',
        '/register'
      ];

      // Check if current path is allowed
      const isAllowedPath = allowedPaths.some(path => 
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );

      if (!isAllowedPath) {
        // Redirect to billing page based on payment status
        if (user.billing.past_due) {
          // If past due, always redirect to billing page
          navigate('/billing-and-plans', { 
            replace: true,
            state: { message: 'Your account is past due. Please update your payment method.' }
          });
        } else if (!user.billing.has_payment_method && user.billing.requires_payment_method) {
          // If no payment method but required, redirect to plan selection
          navigate('/choose-your-plan', { 
            replace: true,
            state: { message: 'Please select a plan and add a payment method to continue.' }
          });
        }
      }
    }
  }, [isLoggedIn, user, navigate, location.pathname]);

  return <>{children}</>;
};

export default BillingGuard;